import { produce } from "immer";
import { t } from "ttag";

import { uuid } from "metabase/lib/uuid";
import type {
  CardDisplayType,
  DatabaseId,
  Dataset,
  VisualizationSettings,
} from "metabase-types/api";

import { MAX_RESULTS_PER_TAB, MAX_TABS } from "../constants";
import type {
  AdhocResult,
  AdhocResultId,
  AdhocTabId,
  AdhocTabRuntime,
  PersistedAdhocTab,
  PersistedWorkbench,
  WorkbenchState,
} from "../types";

/** Default name for the nth query tab, e.g. "Query 3". */
function getDefaultTabName(index: number): string {
  return t`Query ${index}`;
}

/** Default name for the nth result of a tab, e.g. "Result 3". */
function getDefaultResultName(index: number): string {
  return t`Result ${index}`;
}

export const DEFAULT_RESULT_DISPLAY: CardDisplayType = "table";
export const DEFAULT_RESULT_SETTINGS: VisualizationSettings = {
  "table.pivot": false,
};

export type WorkbenchAction =
  | {
      type: "ADD_TAB";
      payload: {
        id: AdhocTabId;
        name?: string;
        databaseId?: DatabaseId | null;
        sql?: string;
      };
    }
  | { type: "DUPLICATE_TAB"; payload: { id: AdhocTabId; newId: AdhocTabId } }
  | { type: "CLOSE_TAB"; payload: { id: AdhocTabId } }
  | { type: "CLOSE_OTHER_TABS"; payload: { id: AdhocTabId } }
  | { type: "RENAME_TAB"; payload: { id: AdhocTabId; name: string } }
  | { type: "SELECT_TAB"; payload: { id: AdhocTabId } }
  | { type: "MOVE_TAB"; payload: { activeId: AdhocTabId; overId: AdhocTabId } }
  | {
      type: "SET_TAB_QUERY";
      payload: {
        id: AdhocTabId;
        databaseId: DatabaseId | null;
        sql: string;
      };
    }
  | {
      type: "SET_TAB_EDITOR_HEIGHT";
      payload: { id: AdhocTabId; height: number };
    }
  | {
      type: "RESULT_STARTED";
      payload: {
        tabId: AdhocTabId;
        resultId: AdhocResultId;
        databaseId: DatabaseId;
        sql: string;
        startedAt: number;
      };
    }
  | {
      type: "RESULT_FINISHED";
      payload: {
        tabId: AdhocTabId;
        resultId: AdhocResultId;
        dataset: Dataset;
        finishedAt: number;
      };
    }
  | {
      type: "RESULT_FAILED";
      payload: {
        tabId: AdhocTabId;
        resultId: AdhocResultId;
        dataset: Dataset;
        finishedAt: number;
      };
    }
  | {
      type: "RESULT_CANCELLED";
      payload: {
        tabId: AdhocTabId;
        resultId: AdhocResultId;
        finishedAt: number;
      };
    }
  | {
      type: "CLOSE_RESULT";
      payload: { tabId: AdhocTabId; resultId: AdhocResultId };
    }
  | {
      type: "SELECT_RESULT";
      payload: { tabId: AdhocTabId; resultId: AdhocResultId };
    }
  | {
      type: "SET_RESULT_DISPLAY";
      payload: {
        tabId: AdhocTabId;
        resultId: AdhocResultId;
        display: CardDisplayType;
        visualizationSettings: VisualizationSettings;
      };
    }
  | {
      type: "SET_RESULT_SETTINGS";
      payload: {
        tabId: AdhocTabId;
        resultId: AdhocResultId;
        visualizationSettings: VisualizationSettings;
      };
    };

/* -------------------------------------------------------------------------
 * Helpers
 * ---------------------------------------------------------------------- */

function createRuntime(): AdhocTabRuntime {
  return { results: [], activeResultId: null, nextResultIndex: 1 };
}

function createTab({
  id,
  name,
  databaseId = null,
  sql = "",
}: {
  id: AdhocTabId;
  name: string;
  databaseId?: DatabaseId | null;
  sql?: string;
}): PersistedAdhocTab {
  return { id, name, databaseId, sql };
}

/**
 * Pick the tab to activate after `removedIndex` was closed: the tab that slid
 * into its place, or the new last tab if the closed one was last.
 */
function getNeighbourId<T extends { id: string }>(
  items: T[],
  removedIndex: number,
): string | null {
  if (items.length === 0) {
    return null;
  }
  return items[Math.min(removedIndex, items.length - 1)].id;
}

/**
 * Keep memory bounded by dropping the oldest *completed* results.
 *
 * A running result is never evicted — its promise is still in flight and would
 * have nowhere to land.
 */
function evictOldResults(runtime: AdhocTabRuntime): void {
  while (runtime.results.length > MAX_RESULTS_PER_TAB) {
    const index = runtime.results.findIndex(
      (result) => result.status !== "running",
    );
    if (index === -1) {
      return;
    }
    const [evicted] = runtime.results.splice(index, 1);
    if (runtime.activeResultId === evicted.id) {
      runtime.activeResultId = getNeighbourId(runtime.results, index);
    }
  }
}

function findResult(
  state: WorkbenchState,
  tabId: AdhocTabId,
  resultId: AdhocResultId,
): AdhocResult | undefined {
  return state.runtimeByTabId[tabId]?.results.find(
    (result) => result.id === resultId,
  );
}

/* -------------------------------------------------------------------------
 * Initial state
 * ---------------------------------------------------------------------- */

export function createInitialState(
  persisted: PersistedWorkbench | null,
): WorkbenchState {
  if (persisted && persisted.tabs.length > 0) {
    const activeTabId = persisted.activeTabId ?? persisted.tabs[0].id;
    return {
      tabs: persisted.tabs,
      activeTabId,
      runtimeByTabId: Object.fromEntries(
        persisted.tabs.map((tab) => [tab.id, createRuntime()]),
      ),
      // Only the restored active tab is mounted; the rest mount lazily when
      // first selected, so they never first-mount hidden.
      visitedTabIds: [activeTabId],
      nextTabIndex: persisted.nextTabIndex,
    };
  }

  const id = uuid();
  const tab = createTab({ id, name: getDefaultTabName(1) });
  return {
    tabs: [tab],
    activeTabId: id,
    runtimeByTabId: { [id]: createRuntime() },
    visitedTabIds: [id],
    nextTabIndex: 2,
  };
}

/* -------------------------------------------------------------------------
 * Reducer
 * ---------------------------------------------------------------------- */

function applyAction(state: WorkbenchState, action: WorkbenchAction): void {
  switch (action.type) {
    case "ADD_TAB": {
      if (state.tabs.length >= MAX_TABS) {
        return;
      }
      const { id, name, databaseId, sql } = action.payload;
      state.tabs.push(
        createTab({
          id,
          name: name ?? getDefaultTabName(state.nextTabIndex),
          databaseId,
          sql,
        }),
      );
      state.nextTabIndex += 1;
      state.runtimeByTabId[id] = createRuntime();
      state.activeTabId = id;
      state.visitedTabIds.push(id);
      return;
    }

    case "DUPLICATE_TAB": {
      const { id, newId } = action.payload;
      const index = state.tabs.findIndex((tab) => tab.id === id);
      if (index === -1 || state.tabs.length >= MAX_TABS) {
        return;
      }
      const source = state.tabs[index];
      state.tabs.splice(index + 1, 0, {
        ...source,
        id: newId,
        name: getDefaultTabName(state.nextTabIndex),
      });
      state.nextTabIndex += 1;
      state.runtimeByTabId[newId] = createRuntime();
      state.activeTabId = newId;
      state.visitedTabIds.push(newId);
      return;
    }

    case "CLOSE_TAB": {
      const { id } = action.payload;
      const index = state.tabs.findIndex((tab) => tab.id === id);
      // The workbench always keeps one tab open, so there is no empty state to
      // design around and no way to end up with nothing selected.
      if (index === -1 || state.tabs.length === 1) {
        return;
      }
      state.tabs.splice(index, 1);
      delete state.runtimeByTabId[id];
      state.visitedTabIds = state.visitedTabIds.filter(
        (visitedId) => visitedId !== id,
      );
      if (state.activeTabId === id) {
        const nextId = getNeighbourId(state.tabs, index);
        state.activeTabId = nextId;
        if (nextId != null && !state.visitedTabIds.includes(nextId)) {
          state.visitedTabIds.push(nextId);
        }
      }
      return;
    }

    case "CLOSE_OTHER_TABS": {
      const { id } = action.payload;
      if (!state.tabs.some((tab) => tab.id === id)) {
        return;
      }
      state.tabs = state.tabs.filter((tab) => tab.id === id);
      state.runtimeByTabId = { [id]: state.runtimeByTabId[id] };
      state.visitedTabIds = [id];
      state.activeTabId = id;
      return;
    }

    case "RENAME_TAB": {
      const tab = state.tabs.find((tab) => tab.id === action.payload.id);
      if (tab) {
        tab.name = action.payload.name;
      }
      return;
    }

    case "SELECT_TAB": {
      const { id } = action.payload;
      if (!state.tabs.some((tab) => tab.id === id)) {
        return;
      }
      state.activeTabId = id;
      if (!state.visitedTabIds.includes(id)) {
        state.visitedTabIds.push(id);
      }
      return;
    }

    case "MOVE_TAB": {
      const { activeId, overId } = action.payload;
      const from = state.tabs.findIndex((tab) => tab.id === activeId);
      const to = state.tabs.findIndex((tab) => tab.id === overId);
      if (from === -1 || to === -1 || from === to) {
        return;
      }
      const [moved] = state.tabs.splice(from, 1);
      state.tabs.splice(to, 0, moved);
      return;
    }

    case "SET_TAB_QUERY": {
      const tab = state.tabs.find((tab) => tab.id === action.payload.id);
      if (tab) {
        tab.databaseId = action.payload.databaseId;
        tab.sql = action.payload.sql;
      }
      return;
    }

    case "SET_TAB_EDITOR_HEIGHT": {
      const tab = state.tabs.find((tab) => tab.id === action.payload.id);
      if (tab) {
        tab.editorHeight = action.payload.height;
      }
      return;
    }

    case "RESULT_STARTED": {
      const { tabId, resultId, databaseId, sql, startedAt } = action.payload;
      const runtime = state.runtimeByTabId[tabId];
      if (!runtime) {
        return;
      }
      const index = runtime.nextResultIndex;
      runtime.nextResultIndex += 1;
      runtime.results.push({
        id: resultId,
        index,
        name: getDefaultResultName(index),
        status: "running",
        databaseId,
        sql,
        display: DEFAULT_RESULT_DISPLAY,
        visualizationSettings: DEFAULT_RESULT_SETTINGS,
        startedAt,
        finishedAt: null,
        dataset: null,
      });
      runtime.activeResultId = resultId;
      evictOldResults(runtime);
      return;
    }

    case "RESULT_FINISHED":
    case "RESULT_FAILED": {
      const { tabId, resultId, dataset, finishedAt } = action.payload;
      const result = findResult(state, tabId, resultId);
      if (!result || result.status !== "running") {
        return;
      }
      result.status = dataset.error != null ? "error" : "completed";
      result.dataset = dataset;
      result.finishedAt = finishedAt;
      return;
    }

    case "RESULT_CANCELLED": {
      const { tabId, resultId, finishedAt } = action.payload;
      const result = findResult(state, tabId, resultId);
      if (!result || result.status !== "running") {
        return;
      }
      result.status = "cancelled";
      result.finishedAt = finishedAt;
      return;
    }

    case "CLOSE_RESULT": {
      const { tabId, resultId } = action.payload;
      const runtime = state.runtimeByTabId[tabId];
      if (!runtime) {
        return;
      }
      const index = runtime.results.findIndex(
        (result) => result.id === resultId,
      );
      if (index === -1) {
        return;
      }
      runtime.results.splice(index, 1);
      if (runtime.activeResultId === resultId) {
        runtime.activeResultId = getNeighbourId(runtime.results, index);
      }
      return;
    }

    case "SELECT_RESULT": {
      const { tabId, resultId } = action.payload;
      const runtime = state.runtimeByTabId[tabId];
      if (runtime?.results.some((result) => result.id === resultId)) {
        runtime.activeResultId = resultId;
      }
      return;
    }

    case "SET_RESULT_DISPLAY": {
      const result = findResult(
        state,
        action.payload.tabId,
        action.payload.resultId,
      );
      if (result) {
        result.display = action.payload.display;
        result.visualizationSettings = action.payload.visualizationSettings;
      }
      return;
    }

    case "SET_RESULT_SETTINGS": {
      const result = findResult(
        state,
        action.payload.tabId,
        action.payload.resultId,
      );
      if (result) {
        result.visualizationSettings = action.payload.visualizationSettings;
      }
      return;
    }
  }
}

/**
 * Pure workbench reducer.
 *
 * Written with immer so untouched tabs and untouched runtimes keep their object
 * identity. `QueryTabPanel` and `ResultPanel` are memoized on exactly those
 * objects, so this structural sharing is what keeps typing in one tab from
 * re-rendering every other tab. `reducer.unit.spec.ts` asserts it explicitly.
 */
export function workbenchReducer(
  state: WorkbenchState,
  action: WorkbenchAction,
): WorkbenchState {
  return produce(state, (draft) => {
    applyAction(draft as WorkbenchState, action);
  });
}
